import axios, { AxiosError, InternalAxiosRequestConfig } from "axios";
import Cookies from "js-cookie";
import { API_BASE_URL } from "./endpoints";
import { HttpError } from "../errors/HttpError";
import { ValidationError } from "../errors/ValidationError";
import { logger } from "../utils/logger";
import { JWT_TOKEN, REFRESH_TOKEN, getLoginUrlWithRedirect } from "../constants/auth";
import {
  cookiesCleanup,
  saveRefreshToken,
  saveUserInfo,
  saveUserToken,
} from "../utils/authStorage";
import { ssoRefreshToken } from "./ssoAuthService";

interface RetryableRequestConfig extends InternalAxiosRequestConfig {
  _retry?: boolean;
}

export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    "Content-Type": "application/json",
  },
});

let isRefreshing = false;
let failedQueue: Array<{
  resolve: (token: string | null) => void;
  reject: (error: unknown) => void;
}> = [];

function processQueue(error: unknown, token: string | null = null): void {
  failedQueue.forEach((promise) => {
    if (error) {
      promise.reject(error);
    } else {
      promise.resolve(token);
    }
  });
  failedQueue = [];
}

apiClient.interceptors.request.use(
  (config) => {
    const jwtToken = Cookies.get(JWT_TOKEN);
    if (jwtToken) {
      config.headers.set("Authorization", `Bearer ${jwtToken}`);
    }
    return config;
  },
  (error) => Promise.reject(error)
);

apiClient.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    logger.error("API request failed", error);

    const originalRequest = error.config as RetryableRequestConfig | undefined;

    if (error.response?.status === 401 && originalRequest && !originalRequest._retry) {
      const refreshToken = Cookies.get(REFRESH_TOKEN);

      if (!refreshToken) {
        cookiesCleanup();
        window.location.href = getLoginUrlWithRedirect();
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise<string | null>((resolve, reject) => {
          failedQueue.push({ resolve, reject });
        }).then((token) => {
          originalRequest.headers.set("Authorization", `Bearer ${token}`);
          return apiClient(originalRequest);
        });
      }

      originalRequest._retry = true;
      isRefreshing = true;

      try {
        const data = await ssoRefreshToken({ refreshToken });
        const newAccessToken = data.accessToken;

        saveUserToken(newAccessToken);
        saveRefreshToken(data.refreshToken, data.refreshTokenExpiresAt);

        const infoPayload = {
          applications: data.applications,
          operatorId: data.operatorId,
          name: data.name,
          location: data.location,
        };
        saveUserInfo(btoa(JSON.stringify(infoPayload)));

        apiClient.defaults.headers.common["Authorization"] = `Bearer ${newAccessToken}`;

        processQueue(null, newAccessToken);
        originalRequest.headers.set("Authorization", `Bearer ${newAccessToken}`);
        return apiClient(originalRequest);
      } catch (refreshError) {
        processQueue(refreshError, null);
        cookiesCleanup();
        window.location.href = getLoginUrlWithRedirect();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (ValidationError.isShape(error)) {
      return Promise.reject(new ValidationError(error));
    }

    return Promise.reject(new HttpError(error));
  }
);
